/**
 * Prompt history recorder — the ONLY writer of `app.prompt.history`.
 *
 * History is derived from `live.diffusion.prompt` settling, not from the action that changed it, so a user edit, a preset load, an undo and a peer's change are all judged by the same rules. Recording at the call sites is what let each prompt UI hold its own idea of what counts as history.
 */
import { createListenerMiddleware } from '@reduxjs/toolkit'
import { morphToPlain } from '~/lib/utils'
import diffusion_schema from '~/schemas/diffusion_schema'
import { isMorphEnvelope } from '~/schemas/morph'
import { recordPrompt } from '~/store/app'
import { selectPromptHistory } from '~/store/app/selectors'
import { liveActions } from '~/store/live'

// Contract first, then shape: `isMorphEnvelope` alone means "is an object", which `diffusion.advanced` also satisfies.
const PROMPT_MORPHS = !!diffusion_schema.properties.prompt.morph

const promptOf = live => live?.diffusion?.prompt

const promptHistory = createListenerMiddleware()

promptHistory.startListening({
	matcher: liveActions,
	effect: async (action, { dispatch, getState, getOriginalState }) => {
		// Undo/redo replay must not record; an inbound wire write carries `skipHistory` too but is told apart by `sync`, and it does.
		if (action.meta?.skipHistory && !action.meta?.sync) return

		const next = promptOf(getState().live)
		// ⚠️ Raw identity, so a settling morph (envelope → string) still passes. Without it, any later live action records the prompt an undo or autoplay deliberately skipped.
		if (next === promptOf(getOriginalState().live)) return
		// Mid-fade the value is an interpolation, not a prompt anyone chose. The destination records when it settles.
		if (PROMPT_MORPHS && isMorphEnvelope(next)) return

		const text = morphToPlain(next)
		// `''` is the contract initial — an absent prompt, never an entry.
		if (!text) return

		// Auto-advance walks presets on a timer; the user chose the sequence, not each prompt in it.
		if (getState().playback.playing) return

		// ⚠️ Compared against the newest ENTRY, not the previous live value: a settling morph moves the live value from envelope to its own destination, and `morphToPlain` reads both as the same string — so a previous-value test skips exactly the write that ends the fade.
		const history = selectPromptHistory(getState())
		if (history[history.length - 1] === text) return

		dispatch(recordPrompt(text))
	},
})

export default promptHistory
